/**
 * Extracts a user-facing message from RTK Query / fetchBaseQuery error payloads.
 * Supports:
 * - Laravel BaseApiException: { error: { detail, validationErrors } }
 * - Laravel ValidationException JSON: { message, errors: { field: string[] } }
 * - JSON string bodies (e.g. PARSING_ERROR)
 */

function firstValidationMessage(ve: Record<string, unknown>): string | null {
  for (const key of Object.keys(ve)) {
    const val = ve[key];
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
      return val[0].trim();
    }
    if (typeof val === 'string') {
      return val.trim();
    }
  }
  return null;
}

/** Try to pull detail from a raw JSON-looking string (fallback when parse failed upstream). */
function extractDetailFromJsonString(text: string): string | null {
  const m = text.match(/"detail"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (m?.[1]) {
    try {
      return JSON.parse(`"${m[1]}"`) as string;
    } catch {
      return m[1].replace(/\\"/g, '"');
    }
  }
  return null;
}

function extractFromResponseBody(data: unknown): string | null {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return extractFromResponseBody(JSON.parse(trimmed));
      } catch {
        return extractDetailFromJsonString(trimmed);
      }
    }
    return extractDetailFromJsonString(trimmed);
  }

  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const d = data as Record<string, unknown>;

  if (typeof d.error === 'string' && d.error.trim()) {
    return d.error.trim();
  }

  // BaseApiException: { error: { detail, validationErrors } }
  if ('error' in d && typeof d.error === 'object' && d.error !== null) {
    const err = d.error as Record<string, unknown>;
    if (typeof err.detail === 'string' && err.detail.trim()) {
      return err.detail.trim();
    }
    if (err.validationErrors && typeof err.validationErrors === 'object' && err.validationErrors !== null) {
      const msg = firstValidationMessage(err.validationErrors as Record<string, unknown>);
      if (msg) {
        return msg;
      }
    }
  }

  // Laravel ValidationException invalidJson: prefer field errors over summary message
  if ('errors' in d && d.errors && typeof d.errors === 'object' && d.errors !== null) {
    const msg = firstValidationMessage(d.errors as Record<string, unknown>);
    if (msg) {
      return msg;
    }
  }
  if (typeof d.message === 'string' && d.message.trim()) {
    return d.message.trim();
  }

  return null;
}

function extractData(error: unknown): unknown {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const e = error as Record<string, unknown>;
  if ('data' in e && e.data !== undefined) {
    return e.data;
  }
  return error;
}

export function getApiErrorMessage(error: unknown): string | null {
  const data = extractData(error);
  if (data !== null) {
    const fromBody = extractFromResponseBody(data);
    if (fromBody) {
      return fromBody;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.trim()) {
      return m.trim();
    }
  }
  return null;
}
